import { useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "@/routes";
import { toast } from "sonner";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Loader2, ChevronLeft, ChevronRight, CheckCircle, Baby } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { prenatalIntakeFormSchema, PrenatalIntakeFormData, calculateRiskFactors } from "./PrenatalIntakeFormSchema";
import { GeneralInfoSection } from "./sections/GeneralInfoSection";
import { CurrentPregnancySection } from "./sections/CurrentPregnancySection";
import { PregnancyHistorySection } from "./sections/PregnancyHistorySection";
import { HealthInfoSection } from "./sections/HealthInfoSection";
import { MentalHealthSection } from "./sections/MentalHealthSection";
import { SocialEnvironmentSection } from "./sections/SocialEnvironmentSection";
import { NeedsAndConcernsSection } from "./sections/NeedsAndConcernsSection";
import { ConsentPreferencesSection } from "./sections/ConsentPreferencesSection";

const STEPS = [
  { id: 1, title: "General Information", component: GeneralInfoSection },
  { id: 2, title: "Current Pregnancy", component: CurrentPregnancySection },
  { id: 3, title: "Pregnancy History", component: PregnancyHistorySection },
  { id: 4, title: "Health Information", component: HealthInfoSection },
  { id: 5, title: "Mental Health & Substance Use", component: MentalHealthSection },
  { id: 6, title: "Social & Environment", component: SocialEnvironmentSection },
  { id: 7, title: "Needs & Concerns", component: NeedsAndConcernsSection },
  { id: 8, title: "Consent & Preferences", component: ConsentPreferencesSection },
];

export function PrenatalIntakeForm() {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const form = useForm<PrenatalIntakeFormData>({
    resolver: zodResolver(prenatalIntakeFormSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      middle_initial: "",
      address_line1: "",
      address_line2: "",
      city: "",
      state: "",
      zip_code: "",
      county: "",
      phone: "",
      email: "",
      preferred_contact_method: "",
      preferred_contact_time: "",
      medicaid_id: "",
      date_of_birth: "",
      ethnicity: "",
      race: [],
      education_level: "",
      marital_status: "",
      emergency_contact_name: "",
      emergency_contact_phone: "",
      is_multiple_pregnancy: false,
      due_date: "",
      first_prenatal_appointment: "",
      has_scheduled_appointment: false,
      receiving_wic: false,
      weight_before_pregnancy: "",
      current_weight: "",
      height: "",
      pregnancy_going_well_notes: "",
      pregnancy_goals: "",
      pregnancy_timing_preference: "",
      planning_to_breastfeed: "",
      breastfeeding_knowledge: "",
      bleeding_or_cramping: false,
      bleeding_cramping_notes: "",
      is_first_pregnancy: false,
      children_in_care: 0,
      living_children_born: 0,
      full_term_births: 0,
      premature_births: 0,
      miscarriages_after_20_weeks: 0,
      miscarriages_before_20_weeks: 0,
      low_birth_weight_babies: 0,
      high_birth_weight_babies: 0,
      last_pregnancy_end_date: "",
      last_pregnancy_outcome: "",
      has_pcp: false,
      pcp_name: "",
      pcp_phone: "",
      health_conditions: [],
      other_health_conditions: "",
      sti_screened: false,
      dental_visits_last_two_years: 0,
      dental_issues: false,
      dental_issues_notes: "",
      tobacco_before_pregnancy: false,
      tobacco_before_pregnancy_notes: "",
      tobacco_during_pregnancy: false,
      tobacco_during_pregnancy_notes: "",
      household_tobacco_use: false,
      alcohol_before_pregnancy: false,
      alcohol_before_pregnancy_amount: "",
      alcohol_during_pregnancy: false,
      alcohol_during_pregnancy_amount: "",
      non_prescribed_drug_use: false,
      feeling_depressed: false,
      stress_level: "",
      mental_health_concerns: false,
      mental_health_concerns_notes: "",
      housing_concerns: false,
      feels_safe_at_home: true,
      food_insecurity: false,
      transportation_barriers: false,
      history_of_abuse: false,
      has_support_system: true,
      support_system_details: "",
      worries: [],
      primary_worry: "",
      coping_strategies: "",
      topics_to_learn: [],
      additional_notes: "",
      is_english_fluent: true,
      consent_given: false,
      member_signature: "",
      visit_location_preference: [],
      communication_consent: [],
    },
  });

  const progress = (currentStep / STEPS.length) * 100;
  const CurrentStepComponent = STEPS[currentStep - 1].component;

  const handleNext = async () => {
    // Validate current step fields before moving forward
    const fieldsToValidate = getFieldsForStep(currentStep);
    const isValid = await form.trigger(fieldsToValidate as any);
    
    if (isValid && currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const getFieldsForStep = (step: number): (keyof PrenatalIntakeFormData)[] => {
    switch (step) {
      case 1:
        return ["first_name", "last_name", "address_line1", "city", "state", "zip_code", "phone", "date_of_birth", "emergency_contact_name", "emergency_contact_phone"];
      case 2:
        return ["due_date"];
      case 3:
        return [];
      case 4:
        return [];
      case 5:
        return [];
      case 6:
        return [];
      case 7:
        return [];
      case 8:
        return ["consent_given", "member_signature"];
      default:
        return [];
    }
  };

  const onSubmit = async (data: PrenatalIntakeFormData) => {
    if (!user) {
      toast.error("You must be logged in to submit this form");
      return;
    }

    setIsSubmitting(true);
    try {
      const riskFactors = calculateRiskFactors(data);
      const dob = new Date(data.date_of_birth);
      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const monthDiff = today.getMonth() - dob.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
        age--;
      }

      const isUnder18 = age < 18;
      const isEligible = isUnder18 || riskFactors >= 4;
      const eligibilityReason = isUnder18 ? "under_18" : (riskFactors >= 4 ? "risk_factors" : "not_eligible");

      const { error } = await supabase.from("prenatal_intake_forms").insert({
        user_id: user.id,
        status: "pending",
        first_name: data.first_name,
        last_name: data.last_name,
        middle_initial: data.middle_initial || null,
        address_line1: data.address_line1,
        address_line2: data.address_line2 || null,
        city: data.city,
        state: data.state,
        zip_code: data.zip_code,
        county: data.county || null,
        phone: data.phone,
        email: data.email || null,
        preferred_contact_method: data.preferred_contact_method || null,
        preferred_contact_time: data.preferred_contact_time || null,
        medicaid_id: data.medicaid_id || null,
        date_of_birth: data.date_of_birth,
        age: age,
        ethnicity: data.ethnicity || null,
        race: data.race || [],
        education_level: data.education_level || null,
        marital_status: data.marital_status || null,
        emergency_contact_name: data.emergency_contact_name,
        emergency_contact_phone: data.emergency_contact_phone,
        is_multiple_pregnancy: data.is_multiple_pregnancy,
        due_date: data.due_date || null,
        first_prenatal_appointment: data.first_prenatal_appointment || null,
        has_scheduled_appointment: data.has_scheduled_appointment,
        receiving_wic: data.receiving_wic,
        weight_before_pregnancy: data.weight_before_pregnancy ? parseFloat(data.weight_before_pregnancy) : null,
        current_weight: data.current_weight ? parseFloat(data.current_weight) : null,
        height: data.height || null,
        pregnancy_going_well_notes: data.pregnancy_going_well_notes || null,
        pregnancy_goals: data.pregnancy_goals || null,
        pregnancy_timing_preference: data.pregnancy_timing_preference || null,
        planning_to_breastfeed: data.planning_to_breastfeed || null,
        breastfeeding_knowledge: data.breastfeeding_knowledge || null,
        bleeding_or_cramping: data.bleeding_or_cramping,
        bleeding_cramping_notes: data.bleeding_cramping_notes || null,
        is_first_pregnancy: data.is_first_pregnancy,
        children_in_care: data.children_in_care,
        living_children_born: data.living_children_born,
        full_term_births: data.full_term_births,
        premature_births: data.premature_births,
        miscarriages_after_20_weeks: data.miscarriages_after_20_weeks,
        miscarriages_before_20_weeks: data.miscarriages_before_20_weeks,
        low_birth_weight_babies: data.low_birth_weight_babies,
        high_birth_weight_babies: data.high_birth_weight_babies,
        last_pregnancy_end_date: data.last_pregnancy_end_date || null,
        last_pregnancy_outcome: data.last_pregnancy_outcome || null,
        has_pcp: data.has_pcp,
        pcp_name: data.pcp_name || null,
        pcp_phone: data.pcp_phone || null,
        health_conditions: data.health_conditions || [],
        other_health_conditions: data.other_health_conditions || null,
        sti_screened: data.sti_screened,
        dental_visits_last_two_years: data.dental_visits_last_two_years,
        dental_issues: data.dental_issues,
        dental_issues_notes: data.dental_issues_notes || null,
        tobacco_before_pregnancy: data.tobacco_before_pregnancy,
        tobacco_before_pregnancy_notes: data.tobacco_before_pregnancy_notes || null,
        tobacco_during_pregnancy: data.tobacco_during_pregnancy,
        tobacco_during_pregnancy_notes: data.tobacco_during_pregnancy_notes || null,
        household_tobacco_use: data.household_tobacco_use,
        alcohol_before_pregnancy: data.alcohol_before_pregnancy,
        alcohol_before_pregnancy_amount: data.alcohol_before_pregnancy_amount || null,
        alcohol_during_pregnancy: data.alcohol_during_pregnancy,
        alcohol_during_pregnancy_amount: data.alcohol_during_pregnancy_amount || null,
        non_prescribed_drug_use: data.non_prescribed_drug_use,
        feeling_depressed: data.feeling_depressed,
        stress_level: data.stress_level || null,
        mental_health_concerns: data.mental_health_concerns,
        mental_health_concerns_notes: data.mental_health_concerns_notes || null,
        housing_concerns: data.housing_concerns,
        feels_safe_at_home: data.feels_safe_at_home,
        food_insecurity: data.food_insecurity,
        transportation_barriers: data.transportation_barriers,
        history_of_abuse: data.history_of_abuse,
        has_support_system: data.has_support_system,
        support_system_details: data.support_system_details || null,
        worries: data.worries || [],
        primary_worry: data.primary_worry || null,
        coping_strategies: data.coping_strategies || null,
        topics_to_learn: data.topics_to_learn || [],
        additional_notes: data.additional_notes || null,
        is_english_fluent: data.is_english_fluent,
        is_eligible: isEligible,
        eligibility_reason: eligibilityReason,
        total_risk_factors: riskFactors,
        consent_given: data.consent_given,
        consent_date: new Date().toISOString(),
        member_signature: data.member_signature,
        member_signature_date: new Date().toISOString().split("T")[0],
        visit_location_preference: data.visit_location_preference || [],
        communication_consent: data.communication_consent || [],
      });

      if (error) throw error;

      setIsSubmitted(true);
      toast.success("Prenatal intake form submitted successfully!");
    } catch (error: any) {
      console.error("Error submitting prenatal intake form:", error);
      toast.error(error.message || "Failed to submit form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardContent className="pt-6">
          <div className="text-center space-y-4">
            <CheckCircle className="h-16 w-16 text-primary mx-auto" />
            <h2 className="text-2xl font-bold text-foreground">Form Submitted Successfully!</h2>
            <p className="text-muted-foreground">
              Thank you for completing the PNCC Pregnancy Questionnaire. A care coordinator will review your information and contact you shortly to discuss next steps.
            </p>
            <Button onClick={() => navigate(ROUTES.MEMBER)} className="mt-4">
              Return to Dashboard
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <div className="flex items-center gap-3">
          <Baby className="h-8 w-8 text-primary" />
          <div>
            <CardTitle className="text-2xl">PNCC Pregnancy Questionnaire</CardTitle>
            <CardDescription>
              Prenatal Care Coordination (PNCC) Intake Form – ForwardHealth
            </CardDescription>
          </div>
        </div>
        
        {/* Progress Indicator */}
        <div className="mt-4 space-y-2">
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>Step {currentStep} of {STEPS.length}: {STEPS[currentStep - 1].title}</span>
            <span>{Math.round(progress)}% Complete</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Step Navigation */}
        <div className="flex flex-wrap gap-1 mt-4">
          {STEPS.map((step) => (
            <button
              key={step.id}
              type="button"
              onClick={() => step.id < currentStep && setCurrentStep(step.id)}
              disabled={step.id > currentStep}
              className={`px-3 py-1 text-xs rounded-full transition-colors ${
                step.id === currentStep
                  ? "bg-primary text-primary-foreground"
                  : step.id < currentStep
                  ? "bg-primary/20 text-primary hover:bg-primary/30 cursor-pointer"
                  : "bg-muted text-muted-foreground cursor-not-allowed"
              }`}
            >
              {step.id}. {step.title}
            </button>
          ))}
        </div>
      </CardHeader>

      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <CurrentStepComponent form={form} />

            <div className="flex justify-between pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={handlePrevious}
                disabled={currentStep === 1}
              >
                <ChevronLeft className="mr-2 h-4 w-4" />
                Previous
              </Button>

              {currentStep === STEPS.length ? (
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <CheckCircle className="mr-2 h-4 w-4" />
                  )}
                  Submit Questionnaire
                </Button>
              ) : (
                <Button type="button" onClick={handleNext}>
                  Next
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              )}
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
